import React, { useEffect, useState } from 'react';
import { RotateCcw, Trophy } from 'lucide-react';
import { TEAM_META, TEAM_IDS } from '../../data/teams';
import { burstConfetti } from '../../utils/confetti';
import { prefersReducedMotion } from '../../utils/motion';

// Animates a number from 0 → target when `run` flips true (skipped, showing the
// final value immediately, when reduced motion is preferred).
function useCountUp(target, run, duration = 900) {
  const [val, setVal] = useState(run ? 0 : target);
  useEffect(() => {
    if (!run) { setVal(target); return undefined; }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setVal(Math.round(p * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run, duration]);
  return val;
}

// Shared end-of-game celebration: a brief "And the winner is…" beat, then the
// winner reveal with a bobbing trophy, counting-up scores, and confetti.
export default function GameOver({ scores, onRestart, restartLabel = 'Play again' }) {
  const reduced = prefersReducedMotion();
  const [revealed, setRevealed] = useState(reduced);

  useEffect(() => {
    if (reduced) return undefined;
    const t = setTimeout(() => setRevealed(true), 900);
    return () => clearTimeout(t);
  }, [reduced]);

  useEffect(() => {
    if (!revealed || !(scores.vikings || scores.panthers)) return undefined;
    burstConfetti();
    const t = setTimeout(burstConfetti, 650); // a second wave for good measure
    return () => clearTimeout(t);
  }, [revealed]); // eslint-disable-line react-hooks/exhaustive-deps

  const vk = useCountUp(scores.vikings || 0, revealed && !reduced);
  const pk = useCountUp(scores.panthers || 0, revealed && !reduced);
  const shown = {
    vikings: reduced ? (scores.vikings || 0) : vk,
    panthers: reduced ? (scores.panthers || 0) : pk,
  };

  const winnerId = scores.vikings === scores.panthers
    ? null
    : (scores.vikings > scores.panthers ? 'vikings' : 'panthers');
  const winner = winnerId ? TEAM_META[winnerId] : null;

  // Suspense beat before the reveal.
  if (!revealed) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-10 sm:p-16 text-center">
        <div className="text-sm font-bold uppercase tracking-[0.3em] text-slate-400 animate-pulse">And the winner is…</div>
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 sm:p-12 text-center"
      style={winner ? { boxShadow: `0 0 0 3px ${winner.soft}, 0 10px 40px rgba(0,0,0,0.06)` } : undefined}
    >
      <div className="tg-winner">
        <Trophy size={52} className={`mx-auto mb-4 ${reduced ? '' : 'tg-trophy'}`} style={{ color: winner ? winner.color : 'var(--accent)' }} />
        {winner ? (
          <>
            <div className="text-sm font-bold uppercase tracking-widest text-slate-400">Winner</div>
            <h3 className="text-3xl sm:text-5xl font-extrabold font-display mt-1" style={{ color: winner.color }}>
              {winner.name}
            </h3>
          </>
        ) : (
          <h3 className="text-3xl font-extrabold font-display text-slate-800">It's a tie!</h3>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 max-w-md mx-auto mt-8">
        {TEAM_IDS.map((id) => (
          <div key={id} className="rounded-xl p-5 border" style={{ borderColor: TEAM_META[id].color, background: TEAM_META[id].soft }}>
            <div className="text-xs font-bold uppercase tracking-wider truncate" style={{ color: TEAM_META[id].color }}>
              {TEAM_META[id].name}
            </div>
            <div className="text-4xl font-extrabold font-display text-slate-900 mt-1">{shown[id]}</div>
          </div>
        ))}
      </div>

      <button
        onClick={onRestart}
        className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition"
      >
        <RotateCcw size={16} /> {restartLabel}
      </button>
    </div>
  );
}
